import { ref } from 'vue'

import { urlSafeFetchInChunks } from '@lib/util/network'
import type { FileRetrieve } from '@openapi'

export interface ResolveFileIdsOptions {
  chunkSize?: number
}

export interface ResolveRequest {
  ids: string[]
  entity: 'file' | 'canvas'
  instance_id: string
}

export interface ResolveResponse {
  objects: Record<string, FileRetrieve | null>
  found_count: number
  total_count: number
}

export interface UseFileInfoOptions {
  listFetcher?: (args: { filters: { id__in: string } }) => Promise<FileRetrieve[] | { results: FileRetrieve[] }>
  fetchById?: (id: FileRetrieve['id']) => Promise<FileRetrieve>
  resolveFetcher?: (request: ResolveRequest) => Promise<ResolveResponse>
  instanceId?: string
}

export default function useFileInfo(options: UseFileInfoOptions = {}) {
  const filesById = ref<Record<string, FileRetrieve>>({})

  async function resolveFileIds(
    ids: FileRetrieve['id'][],
    fetcher?: UseFileInfoOptions['listFetcher'] | UseFileInfoOptions['fetchById'],
    opts: ResolveFileIdsOptions = {},
  ): Promise<FileRetrieve[]> {
    const uniqueIds = Array.from(new Set(ids)).filter(Boolean)
    if (uniqueIds.length === 0) return []

    const chunkSize = opts.chunkSize ?? 100

    // Use resolve API if available and instanceId is provided
    if (options.resolveFetcher && options.instanceId) {
      const response = await options.resolveFetcher({
        ids: uniqueIds,
        entity: 'file',
        instance_id: options.instanceId,
      })

      // Extract non-null files
      const results = Object.values(response.objects).filter(Boolean) as FileRetrieve[]

      // Store in cache
      for (const [id, file] of Object.entries(response.objects)) {
        if (file) {
          filesById.value[id] = file
        }
      }

      return results
    }

    // Fallback to legacy fetchers
    const providedListFetcher =
      typeof fetcher === 'function' ? (fetcher as UseFileInfoOptions['listFetcher']) : options.listFetcher
    const providedFetchById =
      typeof fetcher === 'function' ? (fetcher as UseFileInfoOptions['fetchById']) : options.fetchById

    let fetched: FileRetrieve[] = []

    if (providedListFetcher) {
      fetched = await urlSafeFetchInChunks<FileRetrieve>(
        uniqueIds,
        (chunk) => providedListFetcher({ filters: { id__in: chunk.join(',') } }),
        { chunkSize },
      )
    } else if (providedFetchById) {
      fetched = await urlSafeFetchInChunks<FileRetrieve>(
        uniqueIds,
        async (chunk) => Promise.all(chunk.map((id) => providedFetchById(id as string))),
        { chunkSize },
      )
    } else {
      throw new Error('No fetcher provided. Pass listFetcher, fetchById, or configure resolveFetcher with instanceId.')
    }

    for (const file of fetched) filesById.value[file.id] = file

    return fetched
  }

  return { filesById, resolveFileIds }
}
